import React, { useEffect, useState, useMemo, useCallback } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import { FiArrowLeft, FiEdit } from "react-icons/fi";
import { Link, useParams, useNavigate } from "react-router-dom";
import apiService from "../../actions/apiService";
import { getListingStatus } from "../../utils/listingStatusUtils";
import TransactionIcon from "../../assets/icon/Transaction_status.svg";
import heartIcon from "../../assets/icon/heart-hand.svg";
import comptIcon from "../../assets/icon/pay_compt.svg";
import payDlrIcon from "../../assets/icon/pay_dlr.svg";
import payUsersIcon from "../../assets/icon/pay_users.svg";
import EditListingModal from "../../components/models/EditListingModal";
import ConfirmTransactionDoneModal from "../../components/models/ConfirmTransactionDoneModal";
import TransactionManagementModal from "../../components/models/TransactionManagementModal";

const SellerListingDetails = () => {
  const { id } = useParams();
  const itemId = id || "";
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [progressData, setProgressData] = useState(null);
  const [notifyingBuyer, setNotifyingBuyer] = useState(false);
  const [notifyFeedback, setNotifyFeedback] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmingTransaction, setConfirmingTransaction] = useState(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);

  const computeEscrowAmount = (item) => {
    if (!item) return 0;
    if (Number.isFinite(Number(item.escrowAmount))) {
      return Number(item.escrowAmount);
    }
    const released = Array.isArray(item.transactions)
      ? item.transactions.find((tx) => tx?.status?.toLowerCase() === "released")
      : null;
    if (released) {
      const val = Number(released.seller_payout);
      return Number.isFinite(val) ? val : 0;
    }
    return 0;
  };

  const fetchItemDetails = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiService.get(`/items/${itemId}`);
      if (result.success || result.data) {
        const data = result.data?.item || result.data || result;
        setItem(data);
      } else {
        setError(result.error || "Failed to load item");
      }
    } catch (err) {
      setError("Failed to load item");
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  const fetchProgressData = useCallback(async () => {
    if (!itemId) return;
    try {
      const response = await apiService.get(
        `/transactions/item/${itemId}/progress-step`
      );
      if (response.success) {
        setProgressData(response.data.progress);
      } else {
        console.error("Error fetching progress data:", response.error);
      }
    } catch (error) {
      console.error("Error fetching progress data:", error);
    }
  }, [itemId]);

  useEffect(() => {
    let active = true;
    async function load() {
      await fetchItemDetails();
      if (active) {
        await fetchProgressData();
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [itemId, fetchItemDetails, fetchProgressData]);

  const salePriceValue = useMemo(() => {
    // Prefer accepted purchase request amount if present; otherwise fall back to item price
    const purchaseRequests =
      item?.purchase_requests || item?.purchaseRequests || [];
    const acceptedRequest = Array.isArray(purchaseRequests)
      ? purchaseRequests.find((pr) => pr?.status?.toLowerCase() === "accepted")
      : null;

    const offerValue = Number(acceptedRequest?.offer_amount);
    if (Number.isFinite(offerValue)) return offerValue;

    const itemPrice = Number(item?.price);
    if (Number.isFinite(itemPrice)) return itemPrice;

    return 0;
  }, [item]);

  const hasAcceptedPurchaseRequest = useMemo(() => {
    const purchaseRequests =
      item?.purchase_requests || item?.purchaseRequests || [];
    return Array.isArray(purchaseRequests)
      ? purchaseRequests.some((pr) => pr?.status?.toLowerCase() === "accepted")
      : false;
  }, [item]);

  const escrowAmountValue = useMemo(() => {
    return computeEscrowAmount(item);
  }, [item]);

  const handleNotifyBuyer = async () => {
    setNotifyFeedback("");
    if (!itemId) {
      setNotifyFeedback("Missing item id.");
      return;
    }

    try {
      setNotifyingBuyer(true);
      const res = await apiService.post(
        `/transactions/notify-buyer/${itemId}`,
        {}
      );
      if (res.success) {
        setNotifyFeedback("Buyer notified successfully.");
      } else {
        setNotifyFeedback(res.error || "Failed to notify buyer.");
      }
    } catch (e) {
      setNotifyFeedback("Failed to notify buyer. Please try again.");
    } finally {
      setNotifyingBuyer(false);
    }
  };

  // Handle opening confirm transaction modal
  const handleOpenConfirmModal = () => {
    setConfirmModalOpen(true);
    setConfirmError("");
  };

  // Handle confirm transaction (seller confirms delivery) - called from modal
  const handleConfirmTransaction = async () => {
    if (!listingStatus?.transactionId) {
      setConfirmError("Transaction ID is missing.");
      return;
    }

    setConfirmingTransaction(true);
    setConfirmError("");

    try {
      const res = await apiService.post(
        `/transactions/${listingStatus.transactionId}/confirm-seller`
      );
      if (res.success) {
        // Close modal first
        setConfirmModalOpen(false);

        // Wait a moment for backend to process, then refresh
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Refresh item details to get updated data
        await fetchItemDetails();
        await fetchProgressData();
      } else {
        setConfirmError(res.error || "Failed to confirm transaction.");
      }
    } catch (error) {
      setConfirmError("Failed to confirm transaction. Please try again.");
      console.error("Error confirming transaction:", error);
    } finally {
      setConfirmingTransaction(false);
    }
  };

  // Handle confirm funds reception (seller confirms they received money in bank)
  const handleConfirmFunds = async (transactionId) => {
    if (!transactionId) return;
    try {
      const res = await apiService.post(
        `/transactions/${transactionId}/confirm-funds`
      );
      if (res.success) {
        // Refresh item details to show updated status
        await fetchItemDetails();
        await fetchProgressData();
      } else {
        console.error("Failed to confirm funds:", res.error);
        alert(res.error || "Failed to confirm funds reception");
      }
    } catch (error) {
      console.error("Error confirming funds:", error);
      alert("Error confirming funds reception. Please try again.");
    }
  };

  // Map API step names to icons
  const stepIconMap = {
    "Invite buyers": payUsersIcon,
    "Review offers": TransactionIcon,
    "Transaction active": payDlrIcon,
    "Confirm transfer": heartIcon,
    "Transaction complete": comptIcon,
  };

  const imageBaseUrl = process.env.REACT_APP_IMAGE_URL || "";
  const images = Array.isArray(item?.images) ? item.images : [];
  const imageUrls = images.map((img) => `${imageBaseUrl}${img.file_url}`);

  const mainImageSrc = imageUrls[selectedImageIdx] || "";
  const listingStatus = item ? getListingStatus(item) : null;

  if (loading) {
    return (
      <LayoutBuyerSeller>
        <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
        <div className="relative z-[10] px-5 lg:px-0">
          <div className="flex items-center justify-center py-24">
            <span className="text-[14px] font-[600] text-white lg:text-esblue">
              Loading details...
            </span>
          </div>
        </div>
      </LayoutBuyerSeller>
    );
  }

  if (error || !item) {
    return (
      <LayoutBuyerSeller>
        <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
        <div className="relative z-[10] px-5 lg:px-0">
          <div className="text-[14px] flex items-center justify-start text-white lg:text-esblue font-[600] mb-8">
            <Link
              className="flex items-center justify-center gap-1"
              to={"/seller/listings"}
            >
              <FiArrowLeft className="w-3.5 h-3.5" /> Back to listings
            </Link>
          </div>
          <div className="text-center py-24">
            <p className="text-[16px] text-red-500">
              {error || "Item not found"}
            </p>
          </div>
        </div>
      </LayoutBuyerSeller>
    );
  }

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10] px-5 lg:px-0">
        <div className="text-[14px] flex items-center justify-start text-white lg:text-esblue font-[600] mb-8">
          <Link
            className="flex items-center justify-center gap-1"
            to={"/seller/listings"}
          >
            <FiArrowLeft className="w-3.5 h-3.5" /> Back to listings
          </Link>
        </div>
        <h1 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
          {item?.title || "N/A"}
        </h1>
        <p className="text-[12px] md:text-[14px] opacity-70 text-white lg:text-[#1E1E1E] mt-1">
          Main information and short description of the listing, keywords, year
        </p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-5 items-start">
          {/* Left column */}
          <div className="space-y-5">
            {/* Gallery */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <div className="mb-4 h-[292px] w-full bg-gray-100 rounded-[12px] overflow-hidden relative">
                {mainImageSrc ? (
                  <img
                    src={mainImageSrc}
                    alt="listing"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100" />
                )}
                {listingStatus && (
                  <div className="absolute left-3 top-3">
                    <span
                      className={`text-[14px] font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none ${
                        listingStatus.statusColor || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {listingStatus.statusText}
                    </span>
                  </div>
                )}
              </div>
              {imageUrls.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {imageUrls.slice(0, 3).map((url, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedImageIdx(idx)}
                      className={`h-[104px] cursor-pointer w-full rounded-[12px] overflow-hidden bg-white border ${
                        idx === selectedImageIdx
                          ? "border-esblue"
                          : "border-gray-200"
                      }`}
                    >
                      <img
                        src={url}
                        alt={`thumb-${idx}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Transaction Progress */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-rubik text-big-price md:text-big-price-d text-esdarkGray !font-[500]">
                  Transaction progress
                </h3>
                {progressData ? (
                  <span
                    className={`text-[14px] font-[600] min-w-[100px] px-[10px] py-1 rounded-[50px] ${
                      progressData.overallStatus === "completed"
                        ? "border border-[#16653F] bg-[#BBF7D0] text-[#16653F]"
                        : "border border-[#9BDFF6] bg-[#CBECF9] text-[#003366]"
                    }`}
                  >
                    {progressData.overallStatus === "completed"
                      ? "Completed"
                      : `Step ${progressData.currentStep} of ${progressData.totalSteps}`}
                  </span>
                ) : (
                  <span className="text-[14px] font-[600] border border-[#9BDFF6] bg-[#CBECF9] min-w-[100px] text-[#003366] px-[10px] py-1 rounded-[50px]">
                    Loading...
                  </span>
                )}
              </div>

              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5 mb-8">
                <div className="flex items-center gap-4">
                  {(() => {
                    // Get current step - if completed, show last step, otherwise find active step
                    const getCurrentStep = () => {
                      if (!progressData?.steps) return null;

                      // If overall status is completed, show the last step
                      if (progressData.overallStatus === "completed") {
                        return progressData.steps[
                          progressData.steps.length - 1
                        ];
                      }

                      // Otherwise, find the active step
                      const activeStep = progressData.steps.find(
                        (s) => s.status === "active"
                      );

                      // If no active step but we have steps, show the last one
                      if (!activeStep && progressData.steps.length > 0) {
                        return progressData.steps[
                          progressData.steps.length - 1
                        ];
                      }

                      return activeStep;
                    };

                    const currentStep = getCurrentStep();
                    const stepName = currentStep?.name || "Loading...";
                    const stepDescription =
                      currentStep?.description || "Please wait...";
                    const stepIcon = stepIconMap[stepName] || TransactionIcon;
                    const isCompleted =
                      progressData?.overallStatus === "completed" ||
                      currentStep?.status === "completed";

                    return (
                      <>
                        <div
                          className={`w-[42px] h-[42px] min-w-[42px] min-h-[42px] rounded-full flex items-center justify-center ${
                            isCompleted
                              ? "bg-[#2A8BF7]"
                              : "border border-[#2A8BF7]"
                          }`}
                        >
                          <img
                            src={stepIcon}
                            alt="Current Step Icon"
                            className={
                              isCompleted ? "w-6 h-6 invert" : "opacity-50"
                            }
                          />
                        </div>
                        <div>
                          <p className="font-[600] opacity-75 text-[14px] text-[#1E1E1E]">
                            {stepName}
                          </p>
                          <p className="font-[400] opacity-50 text-[16px] text-[#000025]">
                            {stepDescription}
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Progress Timeline - Horizontal (icon-based) */}
              <div className="flex items-start gap-2 md:gap-2 mb-8">
                {progressData ? (
                  (() => {
                    // Normalize steps so only one is 'active'
                    const normalizeSteps = (steps = [], overallStatus) => {
                      // If overall status is completed, all steps should be completed
                      if (overallStatus === "completed") {
                        return steps.map((step) => ({
                          ...step,
                          status: "completed",
                        }));
                      }

                      let foundActive = false;
                      return steps.map((step) => {
                        if (step.status === "completed") return step;
                        if (step.status === "active") {
                          if (!foundActive) {
                            foundActive = true;
                            return step;
                          }
                          return { ...step, status: "pending" }; // force other 'active' to pending
                        }
                        return step;
                      });
                    };

                    const apiSteps = normalizeSteps(
                      progressData.steps,
                      progressData.overallStatus
                    );

                    const steps = apiSteps.map((step) => ({
                      step: step.id,
                      title: step.name,
                      icon: stepIconMap[step.name] || TransactionIcon,
                      status: step.status,
                    }));

                    const circleBase =
                      "w-[42px] h-[42px] rounded-full flex items-center justify-center shrink-0";
                    const labelBase =
                      "text-[12px] font-[500] text-center hidden md:block mt-2";

                    return steps.map((s, i) => {
                      const isCompleted = s.status === "completed";
                      const isCurrent = s.status === "active";

                      const circleClass = isCurrent
                        ? `${circleBase} border border-[#2A8BF7]`
                        : isCompleted
                        ? `${circleBase} bg-[#2A8BF7]`
                        : `${circleBase} border border-[#1E1E1E80]`;

                      const labelClass = isCurrent
                        ? "text-[#2A8BF7]"
                        : isCompleted
                        ? "text-[#1E1E1E] opacity-50"
                        : "text-[#1E1E1E] opacity-50";

                      const iconClass = isCompleted
                        ? "w-6 h-6 invert"
                        : "w-6 h-6 opacity-50";

                      return (
                        <React.Fragment key={s.step}>
                          <div className="flex flex-col max-w-[70px] items-center">
                            <div className={circleClass}>
                              <img
                                src={s.icon}
                                alt={s.title}
                                className={iconClass}
                              />
                            </div>
                            <p className={`${labelBase} ${labelClass}`}>
                              {s.title}
                            </p>
                          </div>

                          {/* Connector (auto-centered via items-center) */}
                          {i < steps.length - 1 && (
                            <div
                              className={`flex-1 relative mt-5 h-px ${
                                isCompleted
                                  ? "bg-[#2A8BF7]"
                                  : isCurrent
                                  ? "bg-[#2A8BF7] opacity-50 before:content-[''] before:absolute before:right-[-7px] before:top-1/2 before:-translate-y-1/2 before:border-t-[5px] before:border-b-[5px] before:border-l-[7px] before:border-t-transparent before:border-b-transparent before:border-l-[#2A8BF7] before:opacity-50"
                                  : "bg-[#1E1E1E40]"
                              }`}
                            />
                          )}
                        </React.Fragment>
                      );
                    });
                  })()
                ) : (
                  <div className="flex items-center justify-center w-full py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                {progressData?.currentStep >= 2 &&
                  progressData?.overallStatus !== "completed" && (
                    <button
                      onClick={handleNotifyBuyer}
                      disabled={notifyingBuyer}
                      className={`bg-white border border-[#DCEDFF] w-full text-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] font-[600] ${
                        notifyingBuyer ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {notifyingBuyer ? "Notifying..." : "Notify Buyer"}
                    </button>
                  )}
              </div>

              {notifyFeedback && (
                <div className="mt-5">
                  <p
                    className={`text-sm ${
                      notifyFeedback.includes("success")
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {notifyFeedback}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-5">
            {/* Listing details */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                Listing details
              </h3>
              <div className="mt-4">
                {/* <div className="flex items-center justify-between text-[14px]">
                  <span className="text-[16px] text-[#1E1E1E] font-[600]">
                    Item price
                  </span>
                  <span className="text-[18px] text-[#000025] font-[700]">
                    {Number(item?.price || 0).toFixed(2)}{" "}
                    {item?.currency || "EUR"}
                  </span>
                </div> */}
                {listingStatus?.description && (
                  <div className="mt-4">
                    <p className="text-[14px] text-gray-500">
                      {listingStatus.description}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-3">
                {/* Action Buttons based on listing status */}
                {listingStatus?.action === "confirm_transaction" && (
                  <button
                    onClick={handleOpenConfirmModal}
                    className="w-full bg-[#B9FFCC] text-[#16653F] px-4 py-3 rounded-[8px] text-[14px] font-[600] flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {listingStatus.actionText}
                  </button>
                )}
                {listingStatus?.action === "confirm_funds" && (
                  <button
                    onClick={() =>
                      handleConfirmFunds(listingStatus.transactionId)
                    }
                    className="w-full bg-green-500 text-white px-4 py-3 rounded-[8px] text-[14px] font-[600] flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {listingStatus.actionText}
                  </button>
                )}
                {listingStatus?.action === "see_offers" && (
                  <button
                    onClick={() => {
                      navigate("/seller/requests");
                    }}
                    className="w-full bg-[#B9F2FF] text-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] font-[600] flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="20"
                      viewBox="0 0 22 20"
                      fill="none"
                    >
                      <path
                        d="M18 19L21 16M21 16L18 13M21 16H15M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5C17 6.67869 15.9659 8.11585 14.5 8.70924M11 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
                        stroke="#2A8BF7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {listingStatus.actionText}
                  </button>
                )}
                {listingStatus?.action === "send_offers" && (
                  <button
                    onClick={() => {
                      setIsTransactionModalOpen(true);
                    }}
                    className="w-full border border-[#2A8BF7] bg-white text-esblue px-4 py-3 rounded-[8px] text-[14px] font-[600] flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    {listingStatus.actionText}
                  </button>
                )}
                {listingStatus?.action === "completed" &&
                  listingStatus.transactionId && (
                    <Link
                      to={`/seller/transactions/${listingStatus.transactionId}/invoice`}
                      className="w-full border border-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] text-[#2A8BF7] font-[600] flex items-center justify-center gap-2 bg-white hover:bg-blue-50"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="22"
                        viewBox="0 0 18 22"
                        fill="none"
                      >
                        <path
                          d="M11 1.26953V5.40007C11 5.96012 11 6.24015 11.109 6.45406C11.2049 6.64222 11.3578 6.7952 11.546 6.89108C11.7599 7.00007 12.0399 7.00007 12.6 7.00007H16.7305M11 16H5M13 12H5M17 8.98822V16.2C17 17.8802 17 18.7202 16.673 19.362C16.3854 19.9265 15.9265 20.3854 15.362 20.673C14.7202 21 13.8802 21 12.2 21H5.8C4.11984 21 3.27976 21 2.63803 20.673C2.07354 20.3854 1.6146 19.9265 1.32698 19.362C1 18.7202 1 17.8802 1 16.2V5.8C1 4.11984 1 3.27976 1.32698 2.63803C1.6146 2.07354 2.07354 1.6146 2.63803 1.32698C3.27976 1 4.11984 1 5.8 1H9.01178C9.74555 1 10.1124 1 10.4577 1.08289C10.7638 1.15638 11.0564 1.27759 11.3249 1.44208C11.6276 1.6276 11.887 1.88703 12.4059 2.40589L15.5941 5.59411C16.113 6.11297 16.3724 6.3724 16.5579 6.67515C16.7224 6.94356 16.8436 7.2362 16.9171 7.5423C17 7.88757 17 8.25445 17 8.98822Z"
                          stroke="#2A8BF7"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {listingStatus.actionText}
                    </Link>
                  )}

                <button
                  onClick={() => {
                    setEditItem(item);
                    setEditOpen(true);
                  }}
                  className="w-full bg-[#F5F8FF] text-esblue px-4 py-3 rounded-[8px] text-[14px] font-[600] flex items-center justify-center gap-2 "
                >
                  <FiEdit className="w-4 h-4" />
                  Edit listing
                </button>
              </div>
            </div>

            {/* Sales price and In escrow cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-[20px] text-center">
                <p className="text-sm text-[#1E1E1E] mb-4">Sales price</p>
                <p className="text-[24px] font-[700] text-[#2A8BF7]">
                  {`${salePriceValue.toFixed(2)} €`}
                </p>
                {hasAcceptedPurchaseRequest && (
                  <div className="w-full p-1 font-bold rounded-[8px] border border-[#D35100] text-[#D35100] text-sm flex items-center justify-center mt-2">
                    Accepted Offer price
                  </div>
                )}
              </div>
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-[20px] text-center">
                <p className="text-sm text-[#1E1E1E] mb-4">In escrow</p>
                <p className="text-[24px] font-[700] text-[#2A8BF7]">
                  {`${escrowAmountValue.toFixed(2)} €`}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                Description
              </h3>
              <p className="text-[#000025] font-[400] text-[16px] opacity-50 mt-4">
                {item?.description ? item.description : "-"}
              </p>
            </div>

            {/* Specifications */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                Specifications
              </h3>
              <div className="grid mt-4 grid-cols-1 gap-x-10 gap-y-3">
                {item?.plate_number && (
                  <div className="flex items-center justify-between">
                    <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                      Plate number
                    </span>
                    <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                      {item.plate_number}
                    </span>
                  </div>
                )}
                {item?.custom_category && (
                  <div className="flex items-center justify-between">
                    <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                      Category
                    </span>
                    <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                      {item.custom_category}
                    </span>
                  </div>
                )}
                {Object.entries(item?.custom_properties || {}).map(
                  ([k, v], i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                        {k}
                      </span>
                      <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                        {v}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditListingModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        item={editItem}
        onUpdated={async (updated) => {
          if (updated?.id) {
            // Refresh item details from API to ensure all data is up-to-date
            await fetchItemDetails();
            // Refresh progress data if needed
            await fetchProgressData();
            // Close the modal
            setEditOpen(false);
          }
        }}
      />
      <ConfirmTransactionDoneModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmError("");
        }}
        onConfirm={handleConfirmTransaction}
        isLoading={confirmingTransaction}
        errorText={confirmError}
      />
      <TransactionManagementModal
        open={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        transactionData={item}
        itemId={itemId}
        onInviteSent={async () => {
          // Refresh item details after invite is sent
          await fetchItemDetails();
          await fetchProgressData();
        }}
      />
    </LayoutBuyerSeller>
  );
};

export default SellerListingDetails;
